import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import KpiCascadeFilter, {
  kpiMatchesCascadeSteps,
} from "../../../DataManagment/Quantative/component/KpiTagFilter";
import { injectKpiStyles } from "../../../DataManagment/Quantative/component/kpiStyles";
import KpiCreateEditModal from "./KpiCreateEditModal";

/**
 * Searchable multi-select KPI picker.
 *
 * Props:
 *   show           : boolean
 *   onClose        : () => void
 *   selectedKeys   : Array<{ id, source }>   currently-selected picks
 *   onConfirm      : (picks: Array<{ id, title, source, source_kpi_id, source_assessment_id? }>) => void
 *
 * `id` is the picker's synthetic row key (`${source}:${kpi_id}`); source
 * distinguishes 'company' (own KPI bank) from 'riu_backend' (global RIU
 * catalog). Both are surfaced together with badges so composers can mix.
 */
const KpiPickerModal = ({ show, onClose, selectedKeys = [], onConfirm, title }) => {
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState([]);
  const [search, setSearch] = useState("");
  const [filterSteps, setFilterSteps] = useState([]);
  const [picked, setPicked] = useState(
    () => new Set(selectedKeys.map((k) => `${k.source}:${k.id}`))
  );
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    injectKpiStyles();
  }, []);

  useEffect(() => {
    if (!show) return;
    setPicked(new Set(selectedKeys.map((k) => `${k.source}:${k.id}`)));
    setSearch("");
    setFilterSteps([]);
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [riuRes, coRes] = await Promise.all([
        AssessmentService.listKpis(),
        AssessmentService.listCompanyKpis({ limit: 200 }),
      ]);
      if (cancelled) return;
      const riuList = Array.isArray(riuRes?.data?.data)
        ? riuRes.data.data
        : Array.isArray(riuRes?.data)
        ? riuRes.data
        : [];
      const coList = Array.isArray(coRes?.data?.data?.items)
        ? coRes.data.data.items
        : [];
      const riuRows = riuList.map((k) => ({
        key: `riu_backend:${k.id}`,
        id: k.id,
        title: k.title || "(untitled)",
        source: "riu_backend",
        default_weightage: k.default_weightage ?? null,
        moduleName: k.moduleName || null,
        kpi_status: k.kpi_status ?? null,
        kpi_type: k.kpi_type ?? null,
        createdAt: k.createdAt ?? null,
        updatedAt: k.updatedAt ?? null,
        rawTags: k.rawTags ?? null,
      }));
      const coRows = coList.map((k) => ({
        key: `company:${k.id}`,
        id: k.id,
        title:
          k.question_explanation ||
          k.title ||
          k.kpi_name ||
          "(untitled)",
        source: "company",
        default_weightage: k.default_weightage ?? null,
        moduleName: null,
        kpi_status: k.kpi_status ?? null,
        kpi_type: k.kpi_type ?? null,
        createdAt: k.createdAt ?? null,
        updatedAt: k.updatedAt ?? null,
        rawTags: null,
      }));
      setKpis([...coRows, ...riuRows]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const activeSteps = (filterSteps || []).filter((s) => s.values?.size > 0);
    return kpis.filter((k) => {
      if (needle) {
        const inSearch =
          (k.title || "").toLowerCase().includes(needle) ||
          String(k.id || "").includes(needle) ||
          (k.moduleName || "").toLowerCase().includes(needle);
        if (!inSearch) return false;
      }
      if (activeSteps.length && !kpiMatchesCascadeSteps(k, activeSteps)) {
        return false;
      }
      return true;
    });
  }, [kpis, search, filterSteps]);

  const toggle = (key) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /**
   * Emit every checked key. When a picked key has no corresponding row in
   * the picker's display list — e.g. an RIU KPI that got filtered out by
   * the picker's `financialYearId` gate, or a template-scoped KPI the
   * catalog doesn't surface — parse the key and emit a minimal identity
   * object. The caller's state already has the full row (title / source
   * metadata) and will preserve it by identity match.
   */
  const handleConfirm = () => {
    const byKey = new Map(kpis.map((k) => [k.key, k]));
    const objs = Array.from(picked).map((key) => {
      const k = byKey.get(key);
      if (k) {
        return {
          id: k.id,
          title: k.title,
          source: k.source,
          source_kpi_id: k.source === "riu_backend" ? k.id : undefined,
          default_weightage: k.default_weightage ?? null,
        };
      }
      const sepIdx = key.indexOf(":");
      const source = sepIdx > 0 ? key.slice(0, sepIdx) : "company";
      const id = Number(sepIdx > 0 ? key.slice(sepIdx + 1) : key);
      return {
        id,
        title: "",
        source,
        source_kpi_id: source === "riu_backend" ? id : undefined,
        default_weightage: null,
      };
    });
    onConfirm(objs);
    onClose();
  };

  const handleKpiCreated = (saved) => {
    if (!saved?.id) return;
    const row = {
      key: `company:${saved.id}`,
      id: saved.id,
      title:
        saved.question_explanation ||
        saved.title ||
        saved.kpi_name ||
        "(untitled)",
      source: "company",
      moduleName: null,
      kpi_status: saved.kpi_status ?? null,
      kpi_type: saved.kpi_type ?? null,
      createdAt: saved.createdAt ?? null,
      updatedAt: saved.updatedAt ?? null,
      rawTags: null,
    };
    setKpis((prev) => [row, ...prev.filter((k) => k.key !== row.key)]);
    setPicked((prev) => {
      const next = new Set(prev);
      next.add(row.key);
      return next;
    });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{title || "Pick KPIs"}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "65vh", background: "#fafbfc" }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <input
            type="text"
            className="kpi-input"
            placeholder="Search KPIs by title, module or id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, marginBottom: 0 }}
          />
          <button
            type="button"
            className="kpi-btn-primary"
            onClick={() => setCreateOpen(true)}
            style={{ whiteSpace: "nowrap" }}
          >
            + New KPI
          </button>
        </div>
        <div
          style={{
            padding: "10px 12px",
            border: "1px solid var(--rule)",
            borderRadius: 10,
            background: "var(--surface)",
            marginBottom: 12,
          }}
        >
          <KpiCascadeFilter
            kpiList={kpis}
            filterSteps={filterSteps}
            onFilterStepsChange={setFilterSteps}
            lockedSteps={[]}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 4px 8px",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ink-4)",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            fontFamily: "var(--font-mono)",
          }}
        >
          <span>
            {filtered.length} of {kpis.length} shown
          </span>
          <span>
            <span style={{ color: "var(--ink-2)" }}>{picked.size}</span>{" "}
            selected
          </span>
        </div>
        {loading ? (
          <div className="text-center py-4 text-muted">
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        ) : (
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--rule)",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            {filtered.map((k, idx) => {
              const isChecked = picked.has(k.key);
              return (
                <label
                  key={k.key}
                  htmlFor={`kpi-${k.key}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    cursor: "pointer",
                    borderTop: idx === 0 ? "none" : "1px solid #f1f5f9",
                    background: isChecked ? "#f0f9ff" : "transparent",
                    fontSize: 13,
                    color: "var(--ink-2)",
                    fontWeight: isChecked ? 600 : 400,
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    className="kpi-checkbox"
                    id={`kpi-${k.key}`}
                    checked={isChecked}
                    onChange={() => toggle(k.key)}
                  />
                  <span style={{ flex: 1 }}>{k.title || "(untitled)"}</span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      padding: "2px 8px",
                      borderRadius: 4,
                      color:
                        k.source === "company" ? "#065f46" : "#1e40af",
                      background:
                        k.source === "company" ? "#d1fae5" : "#dbeafe",
                    }}
                  >
                    {k.source === "company" ? "MY KPI" : "RIU"}
                  </span>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "24px 12px",
                  color: "var(--ink-4)",
                  fontSize: 13,
                }}
              >
                No KPIs match.
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ background: "var(--surface)", gap: 8 }}>
        <button type="button" className="kpi-btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="kpi-btn-primary"
          onClick={handleConfirm}
        >
          Done ({picked.size})
        </button>
      </Modal.Footer>
      <KpiCreateEditModal
        show={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={handleKpiCreated}
      />
    </Modal>
  );
};

export default KpiPickerModal;
